import React, { Suspense } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, Switch, Redirect, Route } from 'react-router-dom';
import Spinner from 'components/spinner/Spinner';
import UserProvider from 'providers/User';
import { routes } from 'routes/routes';
import * as pages from 'routes/async';
import { ToastContainer } from 'react-toastify';
import { PrivatePrintRoute } from 'components/private-route/PrivatePrintRoute';
import PredictionsEngineProvider from 'providers/PredictionsEngine';
import PredictionsEnginePage from 'pages/predictions-engine/PredictionsEnginePage';
import { PrivateRoute } from './components/private-route/PrivateRoute';
import { PublicRoute } from './components/public-route/PublicRoute';
import history from './utils/history';
import { FilmDetailsProvider } from './pages/film-details/hooks/useFilmDetails';
import { TooltipProvider } from '@/components/ui/tooltip';
import { isUserAuthenticated } from 'services/localstorage';

import 'react-toastify/dist/ReactToastify.css';

const { FilmDetailsPage, FilmPage } = pages;

const App = () => {
  return (
    <BrowserRouter history={history} basename="/">
      <TooltipProvider>
        <UserProvider>
          <ToastContainer autoClose={false} hideProgressBar />

          <Suspense fallback={<Spinner />}>
            <Switch>
              <PrivateRoute
                exact
                path={routes.ROOT}
                component={pages.HomePage}
              />
              <PrivateRoute
                exact
                path={routes.FILM}
                component={() => (
                  <FilmDetailsProvider>
                    <FilmPage />
                  </FilmDetailsProvider>
                )}
                displaySearchFieldComponent
              />
              <PrivateRoute
                exact
                path={routes.CONTACT_US}
                component={pages.ContactUsPage}
                providePublicBrandInfoOnly={!isUserAuthenticated()}
                displayFooterBasedOnPageContent
              />
              <PrivateRoute
                exact
                path={routes.ABOUT}
                component={pages.AboutPage}
                providePublicBrandInfoOnly
                displayFooterBasedOnPageContent
              />
              <PrivateRoute
                exact
                path={routes.LIBRARY}
                component={pages.LibraryPage}
                displaySearchFieldComponent
                subtractFooterHeight
              />
              <PrivateRoute
                exact
                path={routes.ADVANCED_SEARCH}
                component={pages.AdvancedSearchPage}
              />
              <PrivateRoute
                exact
                path={routes.DISTRIBUTORS}
                component={pages.DistributorsPage}
                displaySearchFieldComponent
              />
              <PrivateRoute
                exact
                path={routes.DISTRIBUTOR_FILMS}
                component={pages.DistributorFilmsPage}
                displaySearchFieldComponent
              />
              <PrivateRoute
                exact
                path={routes.FILM_DETAILS}
                component={() => (
                  <FilmDetailsProvider>
                    <FilmDetailsPage />
                  </FilmDetailsProvider>
                )}
                displaySearchFieldComponent
              />
              <PrivateRoute
                exact
                path={routes.SEARCH_RESULTS}
                component={pages.SearchResultsPage}
                displaySearchFieldComponent
                displayFooterBasedOnPageContent
              />
              <PrivateRoute
                exact
                path={routes.DATA_AUDIT}
                component={pages.DataAuditPage}
                displaySearchFieldComponent
              />
              <PrivateRoute
                exact
                path={routes.FILMS_BY_METRIC}
                component={pages.FilmsByMetricPage}
                displaySearchFieldComponent
              />
              <PrivateRoute
                exact
                path={routes.EDIT_PROFILE}
                component={pages.EditProfilePage}
                displaySearchFieldComponent
                displayFooterBasedOnPageContent
              />
              <PrivateRoute
                exact
                path={routes.ACCOUNT}
                component={pages.AccountPage}
                displaySearchFieldComponent
                displayFooterBasedOnPageContent
              />
              <PrivateRoute
                exact
                path={routes.PREDICTIONS}
                component={pages.PredictionsPage}
                displaySearchFieldComponent
                displayFooterBasedOnPageContent
              />
              <PrivateRoute
                path={routes.PREDICTIONS_ENGINE}
                component={() => (
                  <PredictionsEngineProvider>
                    <PredictionsEnginePage />
                  </PredictionsEngineProvider>
                )}
                subtractFooterHeight
              />
              <PrivateRoute
                exact
                path={routes.PERSON}
                component={pages.PersonPage}
                displaySearchFieldComponent
                displayFooterBasedOnPageContent
              />
              <PrivateRoute
                exact
                path={routes.ANALYTICS_REPORT}
                component={pages.AnalyticsReportPage}
                displaySearchFieldComponent
                displayFooterBasedOnPageContent
              />
              <PrivatePrintRoute
                exact
                path={routes.REPORT_PRINT}
                component={pages.ReportPrintPage}
              />
              <PublicRoute
                exact
                path={routes.PRIVACY_POLICY}
                component={pages.PrivacyPolicyPage}
              />
              <PublicRoute
                exact
                path={routes.TERMS_CONDITIONS}
                component={pages.TermsConditionsPage}
              />
              <PublicRoute
                exact
                path={routes.LOGIN}
                component={pages.LoginPage}
              />
              <PublicRoute
                exact
                path={routes.MULTI_FACTOR}
                component={pages.TwoFactorAuthPage}
              />
              {/* <PublicRoute
              exact
              path={routes.MULTI_FACTOR_SETUP}
              component={pages.MultiFactorSetupPage}
            /> */}
              <PublicRoute
                exact
                path={routes.REGISTRATION}
                component={pages.RegistrationPage}
              />
              <PublicRoute
                exact
                path={routes.REGISTRATION_COMPLETED}
                component={pages.RegistrationCompletedPage}
              />
              <PublicRoute
                exact
                path={routes.FORGOT_CREDENTIALS}
                component={pages.ForgotCredentialsPage}
              />
              <PublicRoute
                exact
                path={routes.RESET_PASSWORD}
                component={pages.ResetPasswordPage}
              />
              <PublicRoute
                exact
                path={routes.RESET_PASSWORD_CHECK_EMAIL}
                component={pages.ResetPasswordCheckEmailPage}
              />
              <PublicRoute
                exact
                path={routes.SETUP_PASSWORD}
                component={pages.SetupPasswordPage}
              />
              <PublicRoute
                exact
                path={routes.NEW_PASSWORD_EMAIL_REDIRECT}
                component={pages.NewPasswordEmailRedirectPage}
              />
              <Route exact path="/landing">
                <Redirect to={routes.LOGIN} />
              </Route>
            </Switch>
          </Suspense>
        </UserProvider>
      </TooltipProvider>

      <Toaster />
    </BrowserRouter>
  );
};

export default App;
